/**
 * Real Global Search (STEP 5) fan-out — wires the real
 * `searchPatients` endpoint plus the static Quick Actions catalog into
 * one query, and reports which domains are gracefully disabled
 * (`ENTERPRISE-DASHBOARD-BACKEND-MAPPING.md` §3: Appointments, Bills,
 * Clinical Documents, Orders, and Users all have no search endpoint).
 * Consumed by `layouts/AppLayout/QuickSearch.tsx` (F0's own stub
 * `useQuickSearchResults`, wired for the first time this stage).
 */
import { useEffect, useState } from 'react';
import { useAuthStore } from '@store/auth.store';
import { useEffectiveModuleCodes } from '@hooks/useEffectiveModuleCodes';
import { searchPatients } from '@features/patient/services/patient.service';
import { QUICK_ACTIONS, type QuickAction } from '../quick-actions';
import { searchNavigation } from '@config/navigation-registry';
import type { PatientDto } from '@nexao/contracts';

export interface GlobalSearchResult {
  id: string;
  kind: 'action' | 'patient' | 'page';
  label: string;
  secondary?: string;
  path: string | null;
  action?: QuickAction;
  patient?: PatientDto;
}

export const DISABLED_SEARCH_DOMAINS = [
  { label: 'Appointments', reason: 'No appointment search endpoint exists.' },
  { label: 'Bills / Payments', reason: 'No billing read endpoint exists (ADR-017/018).' },
  { label: 'Clinical Documents', reason: 'No document list/search endpoint exists.' },
  { label: 'Clinical Orders', reason: 'No order list/search endpoint exists.' },
  { label: 'Users', reason: 'Real, but not exposed here — no user-detail page exists yet.' },
] as const;

export function useGlobalSearch(query: string): { results: GlobalSearchResult[]; loading: boolean } {
  const permissions = useAuthStore((state) => state.permissions);
  const moduleCodes = useEffectiveModuleCodes();
  const [patients, setPatients] = useState<PatientDto[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (query.trim().length < 2) {
      setPatients([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    const timer = setTimeout(() => {
      searchPatients({
        criteria: { familyNameContains: query },
        pagination: { page: 1, limit: 8 },
      })
        .then((result) => {
          if (!cancelled) setPatients(result.items);
        })
        .catch(() => {
          if (!cancelled) setPatients([]);
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [query]);

  const lowerQuery = query.trim().toLowerCase();

  const actionResults: GlobalSearchResult[] = QUICK_ACTIONS.filter(
    (action) => action.requiredPermissions.every((permission) => permissions.includes(permission)),
  )
    .filter(
      (action) =>
        lowerQuery.length === 0 ||
        action.label.toLowerCase().includes(lowerQuery) ||
        action.keywords.some((keyword) => keyword.includes(lowerQuery)),
    )
    .map((action) => ({ id: `action:${action.id}`, kind: 'action' as const, label: action.label, path: action.path, action }));

  const patientResults: GlobalSearchResult[] = patients.map((patient) => ({
    id: `patient:${patient.id}`,
    kind: 'patient' as const,
    label: `${patient.givenName} ${patient.familyName ?? ''}`.trim(),
    secondary: `OP Number: ${patient.uhid}`,
    path: `/patients/${patient.id}`,
    patient,
  }));

  // Enterprise Navigation Overhaul — direct navigation to any real (non-Coming-Soon), permission-visible registry page.
  const pageResults: GlobalSearchResult[] = searchNavigation(query, permissions, moduleCodes).map((item) => ({
    id: `page:${item.id}`,
    kind: 'page' as const,
    label: item.label,
    secondary: 'Go to page',
    path: item.path ?? null,
  }));

  return { results: [...pageResults, ...actionResults, ...patientResults], loading };
}
